import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@/common/styles/style";
import { styles } from "./style";
import TimeZoneSelect from "@/molecules/timeZoneSelect";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { Routes } from "@/router/routes";
import { InitializeType, ProfileType } from "../type";
import { initializeProfile } from "../list";
import { sanitizePasswordInput } from "@/helpers/sanitizePasswordInput";
import ErrorMessage from "@/atoms/errorMessage";
import DeleteConfirmModal from "@/organisms/deleteConfirmModal";

interface UserProfileModifyProps {
  userId: number | null;
  userData: InitializeType;
  profileInfo: ProfileType;
  setProfileInfo: React.Dispatch<React.SetStateAction<ProfileType>>;
  setModify: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function UserProfileModify({
  userId,
  userData,
  profileInfo,
  setProfileInfo,
  setModify,
}: UserProfileModifyProps) {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState<{
    [key: number]: boolean;
  }>(Object.fromEntries(profileInfo.map((_, index) => [index, false])));
  const [deleteMessage, setDeleteMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setProfileInfo(initializeProfile(userData));
  }, [userData, setProfileInfo]);

  const handleChange = (index: number, newValue: string | number) => {
    setProfileInfo((prev) =>
      prev.map((info, i) =>
        i === index
          ? {
              ...info,
              value:
                info.title === "タイムゾーン" ? Number(newValue) : newValue,
            }
          : info
      )
    );
  };

  const handleClose = () => {
    setModify(false);
  };

  const handleSave = async () => {
    const password = profileInfo.find(
      (info) => info.title === "パスワード"
    )?.value;
    const pwConfirm = profileInfo.find(
      (info) => info.title === "パスワード (確認用)"
    )?.value;
    const name = profileInfo.find((info) => info.title === "名前")?.value;
    const timezone = profileInfo.find(
      (info) => info.title === "タイムゾーン"
    )?.value;

    if (!name || !timezone || !password || !pwConfirm) {
      setError(
        "名前、タイムゾーン、パスワード、および確認用パスワードを入力してください。"
      );
      return;
    }

    if (password !== pwConfirm) {
      setError("パスワードと確認用パスワードが一致しません。");
      return;
    }

    // パスワードをsanitize Password Inputを使用して検証
    const sanitizedPassword = sanitizePasswordInput(
      password as string,
      setError as React.Dispatch<React.SetStateAction<string | null>>
    );
    if (!sanitizedPassword) {
      setError("無効なパスワードです。");
      return;
    }

    const updatedUser = profileInfo.reduce((acc, { title, value }) => {
      switch (title) {
        case "名前":
          acc.name = value as string;
          break;
        case "メールアドレス":
          acc.email = userData.email;
          break;
        case "タイムゾーン":
          acc.timezone = value as number;
          break;
        case "パスワード":
          acc.password = value as string;
          break;
        case "パスワード (確認用)":
          acc.pwConfirm = value as string;
          break;
        case "テナント":
          acc.tenantName = userData.tenantName;
          break;
        case "グループ":
          acc.groupNameList = userData.groupNameList;
          break;
        case "権限":
          acc.role = userData.role;
          break;
        default:
          break;
      }
      return acc;
    }, {} as InitializeType);

    const requestBody = {
      name: updatedUser.name,
      timezone: updatedUser.timezone,
      password: updatedUser.password,
    };

    try {
      const response = await fetch(`/api/profile/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
        alert("プロフィールが保存されました！");
        setModify(false);
        setError(null);
      } else if (response.status === 404) {
        alert(data.message);
        navigate(Routes.ERROR);
      } else if (response.status === 401 || response.status === 403) {
        alert(data.message);
        navigate(Routes.LOGIN);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
      setError("プロフィールの編集中にエラーが発生しました。");
    }
  };

  const handleOpenModal = () => {
    setDeleteMessage(userData.name);
    setOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/user/${userId}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
        alert("アカウントが削除されました。");
        navigate(Routes.LOGIN);
      } else if (response.status === 404) {
        alert(data.message);
      } else {
        console.error("削除失敗: ", await response.text());
        alert("データ削除に失敗しました。");
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
      alert("エラーが発生しました。");
    }
  };

  const togglePasswordVisibility = (index: number) => {
    setPasswordVisible((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Box>
      <ErrorMessage error={error} />
      {profileInfo
        .map((info, index) => ({ ...info, originalIndex: index }))
        .filter(
          (info) =>
            info.title !== "テナント" &&
            info.title !== "グループ" &&
            info.title !== "権限"
        )
        .map((info) => (
          <Stack
            direction="row"
            spacing={2}
            sx={styles.listBox}
            key={info.title}
          >
            <Typography sx={styles.w30}>{info.title}</Typography>
            {info.title === "タイムゾーン" ? (
              <TimeZoneSelect
                value={info.value as number}
                onChange={(newValue: number) =>
                  handleChange(info.originalIndex, newValue)
                }
              />
            ) : info.title.includes("パスワード") ? (
              <Box sx={styles.pwstyle}>
                <TextField
                  sx={styles.input}
                  type={
                    passwordVisible[info.originalIndex] ? "text" : "password"
                  }
                  value={!info.value ? "" : info.value}
                  fullWidth
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(info.originalIndex, e.target.value)
                  }
                  size="small"
                />
                <IconButton
                  sx={styles.iconButton}
                  onClick={() => togglePasswordVisibility(info.originalIndex)}
                >
                  {passwordVisible[info.originalIndex] ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </Box>
            ) : (
              <TextField
                sx={styles.input}
                value={info.value}
                fullWidth
                disabled={info.title === "メールアドレス"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(info.originalIndex, e.target.value)
                }
                size="small"
              />
            )}
          </Stack>
        ))}
      <Stack direction="row" spacing={10} sx={styles.buttonArea}>
        <Button variant="outlined" onClick={handleClose} sx={styles.button}>
          <Typography variant="button">キャンセル</Typography>
        </Button>
        <Button variant="contained" onClick={handleSave} sx={styles.button}>
          <Typography variant="button">保存</Typography>
        </Button>
        <Button variant="text" onClick={handleOpenModal} sx={styles.button}>
          <Typography variant="button">アカウントの削除</Typography>
        </Button>
      </Stack>
      <DeleteConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        handleDelete={() => handleDelete()}
        deleteMessage={deleteMessage}
        backdrop={{ invisible: true }}
      />
    </Box>
  );
}
